import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  Alert,
  alpha,
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import perfilService from "../services/perfil";
import authService from "../services/auth";
import GerarSenhaButton from "../components/GerarSenhaButton";

const validatePasswordStrength = (password) => {
  const validations = {
    required: password && password.length > 0,
    minLength: password && password.length >= 8,
    hasUppercase: password && /[A-Z]/.test(password),
    hasLowercase: password && /[a-z]/.test(password),
    hasNumber: password && /[0-9]/.test(password),
    hasSpecialChar:
      password && /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
  };

  const errors = [];
  if (!validations.required) errors.push("A senha é obrigatória.");
  if (!validations.minLength)
    errors.push("A senha deve ter pelo menos 8 caracteres.");
  if (!validations.hasUppercase)
    errors.push("A senha deve conter pelo menos uma letra maiúscula.");
  if (!validations.hasLowercase)
    errors.push("A senha deve conter pelo menos uma letra minúscula.");
  if (!validations.hasNumber)
    errors.push("A senha deve conter pelo menos um número.");
  if (!validations.hasSpecialChar)
    errors.push("A senha deve conter pelo menos um caractere especial.");

  return errors;
};

function AlterarSenha() {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    senhaAtual: "",
    novaSenha: "",
    confirmarSenha: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSenhaAtual, setShowSenhaAtual] = useState(false);
  const [showNovaSenha, setShowNovaSenha] = useState(false);
  const [showConfirmarSenha, setShowConfirmarSenha] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!formData.senhaAtual) {
      setError("A senha atual é obrigatória.");
      return;
    }

    const passwordErrors = validatePasswordStrength(formData.novaSenha);
    if (passwordErrors.length > 0) {
      setError(passwordErrors.join(" "));
      return;
    }

    if (formData.novaSenha !== formData.confirmarSenha) {
      setError("As senhas não coincidem.");
      return;
    }

    setLoading(true);

    try {
      await perfilService.alterarSenha({
        senhaAtual: formData.senhaAtual,
        novaSenha: formData.novaSenha,
        confirmarSenha: formData.confirmarSenha,
      });

      setSuccess("Sucesso ao alterar a senha, redirecionando para login...");

      setTimeout(async () => {
        await authService.logout();
        window.location.href = "/login";
      }, 3000);
    } catch (err) {
      setError(
        err.message || err.detail || "Erro ao alterar senha. Tente novamente.",
      );
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        background: `linear-gradient(45deg, ${alpha(
          theme.palette.primary.main,
          0.05,
        )} 0%, ${alpha(theme.palette.primary.light, 0.1)} 100%)`,
        py: { xs: 4, sm: 8 },
        px: { xs: 2, sm: 0 },
      }}
    >
      <Container maxWidth="sm" sx={{ width: "100%" }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 4 },
            borderRadius: 2,
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            maxWidth: { xs: "100%", sm: 480 },
            mx: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <LockIcon
              sx={{
                fontSize: 48,
                color: theme.palette.primary.main,
                mb: 2,
              }}
            />
            <Typography
              component="h1"
              variant="h4"
              sx={{
                mb: 1,
                fontWeight: 700,
                fontSize: { xs: "1.2rem", sm: "1.5rem", md: "2.125rem" },
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Alterar Senha
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
              Preencha os campos abaixo para alterar sua senha
            </Typography>

            {error && (
              <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ width: "100%", mb: 2 }}>
                {success}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ width: "100%" }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                name="senhaAtual"
                label="Senha Atual"
                type={showSenhaAtual ? "text" : "password"}
                value={formData.senhaAtual}
                onChange={handleChange}
                disabled={loading || !!success}
                sx={{ mb: 2 }}
                inputProps={{ maxLength: 128 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowSenhaAtual(!showSenhaAtual)}
                        edge="end"
                        aria-label={
                          showSenhaAtual
                            ? "Ocultar senha atual"
                            : "Mostrar senha atual"
                        }
                      >
                        {showSenhaAtual ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="novaSenha"
                label="Nova Senha"
                type={showNovaSenha ? "text" : "password"}
                value={formData.novaSenha}
                onChange={handleChange}
                disabled={loading || !!success}
                sx={{ mb: 2 }}
                inputProps={{ maxLength: 128 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNovaSenha(!showNovaSenha)}
                        edge="end"
                        aria-label={
                          showNovaSenha
                            ? "Ocultar nova senha"
                            : "Mostrar nova senha"
                        }
                      >
                        {showNovaSenha ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmarSenha"
                label="Confirmar Senha"
                type={showConfirmarSenha ? "text" : "password"}
                value={formData.confirmarSenha}
                onChange={handleChange}
                disabled={loading || !!success}
                sx={{ mb: 3 }}
                inputProps={{ maxLength: 128 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmarSenha(!showConfirmarSenha)
                        }
                        edge="end"
                        aria-label={
                          showConfirmarSenha
                            ? "Ocultar confirmação de senha"
                            : "Mostrar confirmação de senha"
                        }
                      >
                        {showConfirmarSenha ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <GerarSenhaButton
                  disabled={loading || !!success}
                  fullWidth
                  onGenerate={(senha) =>
                    setFormData((prev) => ({
                      ...prev,
                      novaSenha: senha,
                      confirmarSenha: senha,
                    }))
                  }
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading || !!success}
                  sx={{
                    py: 1.5,
                    fontSize: "1rem",
                    textTransform: "none",
                    borderRadius: 2,
                  }}
                >
                  {loading ? "Alterando..." : "Alterar Senha"}
                </Button>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default AlterarSenha;
